"use client";

import { FC, useState } from "react";
import { useTranslations } from "next-intl";
import { Swiper as SwiperComponent, SwiperSlide } from "swiper/react";
import { A11y, Keyboard, Mousewheel } from "swiper/modules";

// eslint-disable-next-line import/no-unresolved
import "swiper/css";

import { GallerySectionRecord } from "@graphql/types/graphql";
import { IconType } from "@types";

import { Modal } from "components/molecules/Modal/Modal";
import { Button } from "components/atoms/Button/Button";
import { Pretitle } from "components/atoms/Pretitle/Pretitle";
import { Container } from "components/organisms/Container/Container";
import { Spacer } from "components/organisms/Spacer/Spacer";

import { ImageBlock } from "../../InlineBlocks/ImageBlock";

import s from "./GallerySection.module.scss";

interface Props {
  data: GallerySectionRecord;
}

export const GallerySection: FC<Props> = ({ data: { title, images } }) => {
  const t = useTranslations("common");
  const [openModal, setOpenModal] = useState(false);

  return (
    <>
      <Container>
        <Spacer size="l">
          {title && <Pretitle title={title} />}
          <div className={s.ImagesGrid}>
            {images.slice(0, 3).map((image) => (
              <div key={image.id} className={s.Image}>
                <ImageBlock data={image} hasReducedMobileHeight />
              </div>
            ))}
            <div className={s.OpenGallery}>
              <Button
                label={t("open-gallery")}
                onClick={() => setOpenModal(!openModal)}
                icon={IconType.Gallery}
                iconSize="l"
                preIcon
              />
            </div>
          </div>
        </Spacer>
      </Container>
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <div className={s.Swiper}>
          <SwiperComponent
            spaceBetween={16}
            slidesPerView="auto"
            direction="horizontal"
            mousewheel
            centeredSlides
            keyboard={{ enabled: true, onlyInViewport: false }}
            breakpoints={{
              768: {
                spaceBetween: 24,
              },
            }}
            modules={[A11y, Keyboard, Mousewheel]}
          >
            {images.map((image) => (
              <SwiperSlide className={s.Slide} key={image.id}>
                <ImageBlock data={image} isFullHeight />
              </SwiperSlide>
            ))}
          </SwiperComponent>
        </div>
      </Modal>
    </>
  );
};
